import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumb from "../../components/Common/Breadcrumb"
import { Container, Row, Col, Card, CardBody, Table, Badge, Button, Modal, ModalBody, ModalHeader } from "reactstrap"
import {
  fetchCancellationsRequest,
  updateCancellationStatusRequest,
} from "../../store/cancellations/actions"
import { db } from "../../helpers/firebase/firebaseConfig"
import { doc, getDoc } from "firebase/firestore"
import Cookies from "js-cookie"
import { toast } from "react-toastify"
import "./cancellation-invoices.css"

const CancellationInvoices = (props) => {
  const dispatch = useDispatch()
  const { cancellations, loading, error } = useSelector(
    (state) => state.cancellations
  )
  const [selectedCancellation, setSelectedCancellation] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState("all")
  const [enrichedCancellations, setEnrichedCancellations] = useState([])
  const [loadingDetails, setLoadingDetails] = useState(false)

  // Get provider ID from cookies
  const providerId = Cookies.get("usersessionId", {
    secure: true,
    sameSite: "strict",
    path: "/",
  })

  // Fetch service details to enrich cancellation data
  const enrichCancellationData = async (cancellationsData) => {
    try {
      setLoadingDetails(true)
      const enriched = await Promise.all(
        cancellationsData.map(async (cancellation) => {
          let servicePrice = cancellation.booking_amount
          let serviceName = "Unknown Service"
          let providerName = cancellation.service_provider_name
          let providerEmail = cancellation.service_provider_email
          let providerPhone = cancellation.service_provider_phone

          // Fetch service details if serviceProviderServices ID exists
          if (cancellation.serviceProviderServices) {
            try {
              const serviceRef = doc(
                db,
                "service_provider_services",
                cancellation.serviceProviderServices
              )
              const serviceDoc = await getDoc(serviceRef)
              if (serviceDoc.exists()) {
                const serviceData = serviceDoc.data()
                serviceName = serviceData?.service_name || serviceName
                servicePrice = serviceData?.price || serviceData?.amount || servicePrice
              }
            } catch (err) {
              console.warn("Error fetching service details:", err)
            }
          }

          // Fetch provider details if needed
          if (cancellation.service_provider_id && !providerName) {
            try {
              const providerRef = doc(
                db,
                "service_providers",
                cancellation.service_provider_id
              )
              const providerDoc = await getDoc(providerRef)
              if (providerDoc.exists()) {
                const providerData = providerDoc.data()
                providerName = providerData?.service_provider_name || providerData?.name
                providerEmail = providerData?.email
                providerPhone = providerData?.phone_number || providerData?.phone
              }
            } catch (err) {
              console.warn("Error fetching provider details:", err)
            }
          }

          // Recalculate percentage fee based on actual service price
          const percentageFee = (servicePrice * 2.4) / 100
          const baseFine = cancellation.base_fine || 0
          const fixedFee = cancellation.fixed_fee || 2
          const totalFine = baseFine + percentageFee + fixedFee

          return {
            ...cancellation,
            serviceName,
            servicePrice,
            percentageFee: parseFloat(percentageFee.toFixed(2)),
            totalFine: parseFloat(totalFine.toFixed(2)),
            providerName,
            providerEmail,
            providerPhone,
            feeCalculationBreakdown: `${servicePrice} × 2.4% = ${parseFloat(
              percentageFee.toFixed(2)
            )} SAR`,
          }
        })
      )
      setEnrichedCancellations(enriched)
      setLoadingDetails(false)
    } catch (err) {
      console.error("Error enriching cancellation data:", err)
      setEnrichedCancellations(cancellationsData)
      setLoadingDetails(false)
    }
  }

  useEffect(() => {
    if (providerId) {
      dispatch(fetchCancellationsRequest(providerId))
    }
  }, [providerId, dispatch])

  // Enrich data whenever cancellations change
  useEffect(() => {
    if (cancellations && cancellations.length > 0) {
      enrichCancellationData(cancellations)
    } else {
      setEnrichedCancellations([])
    }
  }, [cancellations])

  // Filter cancellations by status (use enriched data)
  const filteredCancellations = enrichedCancellations.filter((c) => {
    if (filterStatus === "all") return true
    if (filterStatus === "deducted") return c.deduction_status === "deducted"
    return c.status === filterStatus
  })

  // Status change handler
  const handleStatusChange = (cancellationId, newStatus) => {
    dispatch(updateCancellationStatusRequest(cancellationId, newStatus))
    setShowModal(false)
  }

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending":
        return "warning"
      case "confirmed":
        return "success"
      case "disputed":
        return "danger"
      default:
        return "secondary"
    }
  }

  // Get cancellation type badge
  const getCancellationTypeBadge = (type) => {
    return type === "early_cancellation" ? "info" : "danger"
  }

  // Get deduction status badge
  const getDeductionStatusBadge = (deductionStatus) => {
    switch (deductionStatus) {
      case "deducted":
        return { color: "success", text: "مخصوم" }
      case "pending":
        return { color: "warning", text: "قيد الانتظار" }
      case "failed":
        return { color: "danger", text: "فشل الخصم" }
      default:
        return { color: "secondary", text: "غير محدد" }
    }
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return "N/A"
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumb
          maintitle="فواتير الإلغاء"
          title="إدارة"
          breadcrumbItem="فواتير الإلغاء"
        />

        <Container fluid>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">قائمة فواتير الإلغاء</h4>
                    <div className="filter-buttons">
                      <Button
                        color={filterStatus === "all" ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => setFilterStatus("all")}
                        className="me-2"
                      >
                        الكل ({cancellations.length})
                      </Button>
                      <Button
                        color={
                          filterStatus === "pending" ? "primary" : "secondary"
                        }
                        size="sm"
                        onClick={() => setFilterStatus("pending")}
                        className="me-2"
                      >
                        قيد الانتظار (
                        {cancellations.filter((c) => c.status === "pending")
                          .length}
                        )
                      </Button>
                      <Button
                        color={
                          filterStatus === "confirmed"
                            ? "primary"
                            : "secondary"
                        }
                        size="sm"
                        onClick={() => setFilterStatus("confirmed")}
                        className="me-2"
                      >
                        مؤكد (
                        {cancellations.filter((c) => c.status === "confirmed")
                          .length}
                        )
                      </Button>
                      <Button
                        color={
                          filterStatus === "disputed" ? "primary" : "secondary"
                        }
                        size="sm"
                        onClick={() => setFilterStatus("disputed")}
                        className="me-2"
                      >
                        معترض (
                        {cancellations.filter((c) => c.status === "disputed")
                          .length}
                        )
                      </Button>
                      <Button
                        color={
                          filterStatus === "deducted" ? "primary" : "secondary"
                        }
                        size="sm"
                        onClick={() => setFilterStatus("deducted")}
                      >
                        مخصوم (
                        {cancellations.filter((c) => c.deduction_status === "deducted")
                          .length}
                        )
                      </Button>
                    </div>
                  </div>

                  {loading || loadingDetails ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">جاري التحميل...</span>
                      </div>
                      <p className="mt-2">جاري تحميل البيانات...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger">
                      خطأ في تحميل البيانات: {error}
                    </div>
                  ) : filteredCancellations.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>الخدمة</th>
                            <th>سعر الخدمة</th>
                            <th>تاريخ الإلغاء</th>
                            <th>النوع</th>
                            <th>ساعات مضت</th>
                            <th>رسم 2.4%</th>
                            <th>الإجمالي</th>
                            <th>حالة الخصم</th>
                            <th>سبب الخصم</th>
                            {/* <th>الحالة</th> */}
                            <th>الإجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCancellations.map((cancellation, index) => (
                            <tr key={cancellation.id}>
                              <td>{index + 1}</td>
                              <td>
                                <small className="text-muted">
                                  {cancellation.service_name}
                                </small>
                              </td>
                              <td className="fw-bold">
                                {cancellation.servicePrice} ر.س
                              </td>
                              <td>
                                {formatDate(cancellation.cancellation_date)}
                              </td>
                              <td>
                                <Badge
                                  color={getCancellationTypeBadge(
                                    cancellation.cancellation_type
                                  )}
                                >
                                  {cancellation.cancellation_type ===
                                  "early_cancellation"
                                    ? "إلغاء مبكر"
                                    : "إلغاء متأخر"}
                                </Badge>
                              </td>
                              <td>{cancellation.hours_elapsed}h</td>
                              <td className="text-info fw-bold">
                                {cancellation.percentageFee} ر.س
                              </td>
                              <td className="fw-bold text-danger">
                                {cancellation.totalFine} ر.س
                              </td>
                              <td>
                                <Badge
                                  color={getDeductionStatusBadge(
                                    cancellation.deduction_status
                                  ).color}
                                >
                                  {getDeductionStatusBadge(
                                    cancellation.deduction_status
                                  ).text}
                                </Badge>
                              </td>
                              <td>
                                <small className="text-muted">{cancellation.deduction_reason || '-'}</small>
                              </td>
                              {/* <td>
                                <Badge
                                  color={getStatusBadgeColor(
                                    cancellation.status
                                  )}
                                >
                                  {cancellation.status === "pending"
                                    ? "قيد الانتظار"
                                    : cancellation.status === "confirmed"
                                    ? "مؤكد"
                                    : "معترض"}
                                </Badge>
                              </td> */}
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  outline
                                  onClick={() => {
                                    setSelectedCancellation(cancellation)
                                    setShowModal(true)
                                  }}
                                  className="me-2"
                                >
                                  التفاصيل
                                </Button>
                                {/* {cancellation.status === "pending" && (
                                  <>
                                    <Button
                                      color="success"
                                      size="sm"
                                      outline
                                      onClick={() =>
                                        handleStatusChange(
                                          cancellation.id,
                                          "confirmed"
                                        )
                                      }
                                      className="me-1"
                                      title="تأكيد الفاتورة"
                                    >
                                      ✓
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      outline
                                      onClick={() =>
                                        handleStatusChange(
                                          cancellation.id,
                                          "disputed"
                                        )
                                      }
                                      title="الاعتراض على الفاتورة"
                                    >
                                      !
                                    </Button>
                                  </>
                                )} */}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted">لا توجد فواتير إلغاء</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Statistics Cards */}
          {filteredCancellations.length > 0 && (
            <Row className="mt-4">
              <Col md="3">
                <Card className="card-statistics">
                  <CardBody>
                    <h5>إجمالي الفواتير</h5>
                    <h3 className="text-primary">
                      {filteredCancellations.length}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-statistics">
                  <CardBody>
                    <h5>إجمالي الرسوم</h5>
                    <h3 className="text-danger">
                      {filteredCancellations
                        .filter(c => c.deduction_status !== "deducted")
                        .reduce((sum, c) => sum + (c.total_fine || 0), 0)
                        .toFixed(2)}{" "}
                      ر.س
                    </h3>
                    <small className="text-muted">
                      (غير شاملة المبالغ المخصومة)
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-statistics">
                  <CardBody>
                    <h5>إلغاءات مبكرة</h5>
                    <h3 className="text-info">
                      {filteredCancellations.filter(
                        (c) => c.cancellation_type === "early_cancellation"
                      ).length}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="card-statistics">
                  <CardBody>
                    <h5>إلغاءات متأخرة</h5>
                    <h3 className="text-warning">
                      {filteredCancellations.filter(
                        (c) => c.cancellation_type === "late_cancellation"
                      ).length}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>

        {/* Details Modal */}
        <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg">
          <ModalHeader toggle={() => setShowModal(false)}>
            تفاصيل فاتورة الإلغاء
          </ModalHeader>
          <ModalBody>
            {selectedCancellation && (
              <div className="cancellation-details">
                {/* Order Info */}
                <div className="detail-section">
                  <h6 className="section-title">معلومات الطلب والخدمة</h6>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>رقم الطلب:</strong>{" "}
                        {selectedCancellation.order_id}
                      </p>
                      <p>
                        <strong>اسم الخدمة:</strong>{" "}
                        {selectedCancellation.service_name}
                      </p>
                      <p>
                        <strong>سعر الخدمة:</strong>{" "}
                        {selectedCancellation.servicePrice} ر.س
                      </p>
                    </Col>
                    <Col md="6">
                      {/* <p>
                        <strong>تاريخ الطلب:</strong>{" "}
                        {formatDate(selectedCancellation.order_created_at)}
                      </p> */}
                      <p>
                        <strong>النوع:</strong>{" "}
                        <Badge
                          color={getCancellationTypeBadge(
                            selectedCancellation.cancellation_type
                          )}
                        >
                          {selectedCancellation.cancellation_type ===
                          "early_cancellation"
                            ? "إلغاء مبكر (بعد 24 ساعة)"
                            : "إلغاء متأخر (قبل 24 ساعة)"}
                        </Badge>
                      </p>
                    </Col>
                  </Row>
                </div>

                {/* Provider Info */}
                {/* <div className="detail-section mt-4">
                  <h6 className="section-title">معلومات مزود الخدمة</h6>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>الاسم:</strong>{" "}
              
                        {selectedCancellation.providerName || selectedCancellation.service_provider_name}
                      </p>
                      <p>
                        <strong>البريد الإلكتروني:</strong>{" "}
                        {selectedCancellation.providerEmail || selectedCancellation.service_provider_email}
                      </p>
                    </Col>
                    <Col md="6">
                      <p>
                        <strong>الشركة:</strong>{" "}
                        {selectedCancellation.service_provider_company}
                      </p>
                      <p>
                        <strong>الهاتف:</strong>{" "}
                        {selectedCancellation.service_provider_phone}
                      </p>
                    </Col>
                  </Row>
                </div> */}

                {/* Fee Breakdown */}
                <div className="detail-section mt-4">
                  <h6 className="section-title">تفاصيل الرسوم والحسابات</h6>
                  <div className="fee-breakdown">
                    <Row className="mb-3">
                      <Col md="12">
                        <div className="alert alert-info mb-0">
                          <strong>حساب رسم النسبة المئوية (2.4%):</strong>
                          <div className="mt-2">
                            <p>سعر الخدمة: <strong>{selectedCancellation.servicePrice} ر.س</strong></p>
                            <p>الصيغة: {selectedCancellation.servicePrice} × 2.4% = <strong className="text-success">{selectedCancellation.percentageFee} ر.س</strong></p>
                            {selectedCancellation.feeCalculationBreakdown && (
                              <p className="mb-0">التفصيل: {selectedCancellation.feeCalculationBreakdown}</p>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <Table borderless size="sm" className="fee-table mt-3">
                      <tbody>
                        <tr className="border-bottom">
                          <td>
                            <strong>رسم النسبة المئوية (2.4%):</strong>
                          </td>
                          <td className="text-end">
                            <strong className="text-info">{selectedCancellation.percentageFee} ر.س</strong>
                          </td>
                        </tr>
                        <tr className="border-bottom">
                          <td>
                            <strong>الرسم الثابت:</strong>
                          </td>
                          <td className="text-end">
                            <strong>{selectedCancellation.fixed_fee || 2} ر.س</strong>
                          </td>
                        </tr>
                        {selectedCancellation.base_fine > 0 && (
                          <tr className="border-bottom">
                            <td>
                              <strong>غرامة الإلغاء المتأخر:</strong>
                            </td>
                            <td className="text-end">
                              <strong className="text-warning">{selectedCancellation.base_fine} ر.س</strong>
                            </td>
                          </tr>
                        )}
                        <tr className="border-top border-bottom bg-light">
                          <td>
                            <strong>الإجمالي المستحق:</strong>
                          </td>
                          <td className="text-end">
                            <strong className="text-danger" style={{ fontSize: "1.2rem" }}>
                              {selectedCancellation.totalFine} ر.س
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {selectedCancellation.notes && (
                      <p className="mt-3 mb-0">
                        <strong>ملاحظات:</strong> {selectedCancellation.notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Cancellation Details */}
                <div className="detail-section mt-4">
                  <h6 className="section-title">معلومات الإلغاء</h6>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>تاريخ الإلغاء:</strong>{" "}
                        {formatDate(selectedCancellation.cancellation_date)}
                      </p>
                      <p>
                        <strong>الوقت المنقضي:</strong>{" "}
                        {selectedCancellation.hours_elapsed} ساعة
                      </p>
                      <p>
                        <strong>سبب الإلغاء:</strong>{" "}
                        {selectedCancellation.cancellation_reason}
                      </p>
                      <p>
                        <strong>سبب الخصم:</strong>{" "}
                        {selectedCancellation.deduction_reason || '-'}
                      </p>
                      {selectedCancellation.added_to_platform_revenue > 0 && (
                        <p className="text-muted">
                          <strong>تمت إضافة إلى إيراد المنصة:</strong>{" "}
                          {Number(selectedCancellation.added_to_platform_revenue).toFixed(2)} ر.س
                        </p>
                      )}
                    </Col>
                    <Col md="6">
                      <p>
                        <strong>حالة الخصم:</strong>{" "}
                        <Badge
                          color={getDeductionStatusBadge(
                            selectedCancellation.deduction_status
                          ).color}
                        >
                          {getDeductionStatusBadge(
                            selectedCancellation.deduction_status
                          ).text}
                        </Badge>
                      </p>
                      {selectedCancellation.deduction_status === "deducted" && (
                        <>
                          <p>
                            <strong>تاريخ الخصم:</strong>{" "}
                            {formatDate(selectedCancellation.deducted_at)}
                          </p>
                          <p className="text-success">
                            <strong>✓ تم خصم المبلغ من حسابك</strong>
                          </p>
                        </>
                      )}
                      {selectedCancellation.deduction_status === "pending" && (
                        <p className="text-warning">
                          <strong>⏳ في انتظار الخصم من حسابك</strong>
                        </p>
                      )}
                      {selectedCancellation.deduction_status === "failed" && (
                        <p className="text-danger">
                          <strong>✗ فشل خصم المبلغ من حسابك</strong>
                        </p>
                      )}
                    </Col>
                  </Row>

                  {Array.isArray(selectedCancellation.deducted_from_invoices) && selectedCancellation.deducted_from_invoices.length > 0 && (
                    <div className="mt-3">
                      <h6 className="section-title">تفاصيل الخصم من الفواتير</h6>
                      <Table bordered size="sm">
                        <thead>
                          <tr>
                            <th>رقم الفاتورة</th>
                            <th>المبلغ المخصوم</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCancellation.deducted_from_invoices.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.invoice_id || item.id || '-'}</td>
                              <td className="text-danger">{Number(item.amount || item.deducted || 0).toFixed(2)} ر.س</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  )
}

export default CancellationInvoices
